import EditorJS from "@editorjs/editorjs";

// Define an object mapping block types to their corresponding dynamic import functions
const blocksUsed = {
    header: () => import("@editorjs/header"),
    delimiter: () => import("@editorjs/delimiter"),
    link: () => import("@editorjs/link"),
    RawTool: async () => {
        const RawTool = await import("@editorjs/raw");
        return {
            default: class CustomRawTool extends RawTool.default {
                render() {
                    const wrapper = document.createElement("div");
                    const htmlContent = this.data.html || this.data.text || "";
                    
                    // Insert HTML content
                    wrapper.innerHTML = htmlContent;
                    
                    // Only run script extraction and execution if there are actual script tags
                    if (htmlContent.includes("<script")) {
                        // Dynamically import script utilities only when needed
                        import("./scriptExecutor.js").then(({ extractScriptData, executeScriptsAfterInteraction }) => {
                            const scriptData = extractScriptData(wrapper);
                            if (scriptData.length > 0) {
                                executeScriptsAfterInteraction(scriptData, wrapper);
                            }
                        });
                    }
                    return wrapper;
                }
            }
        };
    },
    quote: async () => {
        const Quote = await import("@editorjs/quote");
        return {
            default: class CustomEditorJSQuote extends Quote.default {
                render() {
                    this.element = Quote.default.prototype.render.call(this);

                    if (!this.data?.caption) {
                        this.element.removeChild(this.element.lastChild);
                    }

                    return this.element;
                }
            },
        };
    },
    table: () => import("@editorjs/table"),
    checklist: () => import("@editorjs/checklist"),
    image: () => import("./editorjs/GalleryImage"),
    nestedList: () => import("@editorjs/nested-list"),
    list: () => import("./editorjs/CustomEditorList.js"),
    embed: async () => {
        const Embed = await import("@editorjs/embed");
        return {
            default: class CustomEditorJSEmbed extends Embed.default {
                render() {
                    this.element = Embed.default.prototype.render.call(this);

                    if (!this.data.caption) {
                        this.element.removeChild(this.element.lastChild);
                    }

                    return this.element;
                }
            },
        };
    },
    customForm: () => import("./editorjs/CustomForm"),
    customerReview: () => import("./editorjs/CustomerReview"),
    reusableBlock: () => import("./editorjs/ReusableBlock"),
    marker: () => import("@editorjs/marker"),
    alert: () => import("editorjs-alert"),
    faqsBlock: () => import("./editorjs/FAQsBlock.js"),
    iframe: async () => {
        const Iframe = (await import("@hammaadhrasheedh/editorjs-iframe")).default || (await import("@hammaadhrasheedh/editorjs-iframe"));
        return {
            default: class CustomIframeTool extends Iframe {
                render() {
                    const wrapper = super.render();
                    setTimeout(() => {
                        const iframe = wrapper.querySelector("iframe");
                        if (iframe) {
                            iframe.setAttribute("loading", "lazy");
                        }
                    }, 0);
                    return wrapper;
                }
            }
        };
    },
};

// Getting tools in formatted way
function getModuleValues(module, blockName) {
    let values = null;
    switch (blockName) {
    case "table":
        values = {
            class: module.default || module,
            inlineToolbar: true,
        };
        break;

    case "image":
        values = {
            class: module.default || module,
            config: { readOnly: true },
        };
        break;

    case "marker":
        values = {
            class: module.default || module,
            shortcut: "CMD+SHIFT+M",
        };
        break;

    case "list":
        values = {
            class: module.default || module,
            inlineToolbar: true,
            config: {
                defaultStyle: "unordered",
            },
        };
        break;
    case "faqsBlock":
        values = {
            class: module.default || module,
            config: { readOnly: true },
        };
        break;

    default:
        values = {
            class: module.default || module,
        };
        break;
    }
    return values;
}

// Function to fetch and configure tools dynamically based on the block names used in the editor
async function getBlockConfig(blockNames) {
    const result = {};
    for (const blockName of blockNames) {
        if (blocksUsed[blockName]) {
            // Import the componnets dynamically
            const module = await blocksUsed[blockName]();
            result[blockName] = getModuleValues(module, blockName);
        }
    }

    return result;
}



// Function to initialize the EditorJS instance
async function initializeEditor(blocks = {}) {
    let dataBlock = {};

    if (Object.keys(blocks).length === 0) {
        const blocksData = document.getElementById("blocks").value || "{}";
        dataBlock = JSON.parse(blocksData.replace("&quot;", "") || "{}");
    } else {
        dataBlock = JSON.parse(blocks);
    }

    // Get the different block types used in the overall editor
    const blockTypes = dataBlock.blocks.map((block) => block.type);

    // Get only unique block types from used components
    const uniqueBlockTypes = [...new Set(blockTypes)];

    // Get the used tools dynamically in required format
    const tools = await getBlockConfig(uniqueBlockTypes);

    window.editor = new EditorJS({
        holder: "editorjs",
        readOnly: true,
        data: dataBlock,
        tools: tools,
    });
}

// Make it accessible globally
window.reInitializeEditor = initializeEditor;
